import { initContract } from '@ts-rest/core';
import { createApi } from './ts-rest';
import type { CopilotMessage, ServerSideCopilotToolPayload } from '@customerio/hydra-copilot';
import type { RawAccount, RawUser, Workspace } from '../auth/types';
import type { AIContext, AIContextOverrides } from '@/features/ai-business-profile/types';
import { crossAppCache } from '@/utils/cross-app-cache';

const c = initContract();
export const contract = c.router({
  /**
   * Account API
   */
  getAccount: {
    method: 'GET',
    path: '/accounts/current',
    responses: {
      200: c.type<{
        account: RawAccount;
        environments: Workspace[];
        user: RawUser;
        session_expires_at: number;
      }>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountById: {
    method: 'GET',
    path: '/accounts/:accountId',
    pathParams: c.type<{ accountId: number }>(),
    responses: {
      200: c.type<{
        account: RawAccount;
        environments: Workspace[];
        user: RawUser;
        session_expires_at: number;
      }>(),
    },
    summary: 'Get account data based on account ID',
  },
  getAccountByEnvironmentId: {
    method: 'GET',
    path: '/account_by_environment/:environmentId',
    pathParams: c.type<{ environmentId: number }>(),
    responses: {
      200: c.type<{
        account: RawAccount;
        environments: Workspace[];
        user: RawUser;
        session_expires_at: number;
      }>(),
    },
    summary: 'Get account data based on environment ID',
  },

  /**
   * Notifications API
   */
  getNotifications: {
    method: 'GET',
    path: '/accounts/:accountId/notifications',
    pathParams: c.type<{ accountId: number }>(),
    responses: {
      200: c.type<{
        notifications: Array<{
          id: number;
          title: string;
          body: string;
          link_text: string;
          link_url: string;
          activated: number;
          created: number;
          updated: number;
          viewed: number;
        }>;
      }>(),
    },
    summary: 'Get all notifications',
  },
  markNotificationsAsViewed: {
    method: 'POST',
    path: '/accounts/:accountId/notifications/mark_as_viewed',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<{
      ids: number[];
    }>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Mark notifications as viewed',
  },

  /**
   * Copilot API
   */
  createCopilotSession: {
    method: 'POST',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<object>(),
    responses: {
      200: c.type<{
        session_id: string;
        tools: ServerSideCopilotToolPayload[];
      }>(),
    },
    summary: 'Start a new copilot session',
  },
  deleteCopilotSession: {
    method: 'DELETE',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<{ accountId: number; sessionId: number }>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Stop a copilot session',
  },
  getCopilotSession: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<{ accountId: number; sessionId: number }>(),
    responses: {
      200: c.type<{
        name: string;
        messages: CopilotMessage[];
        tools: ServerSideCopilotToolPayload[];
      }>(),
    },
    summary: 'Get messages from a copilot session',
  },
  getCopilotFileUploadSignature: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/sign',
    pathParams: c.type<{ accountId: number; sessionId: string }>(),
    body: c.type<{
      size: string;
      mime_type: string;
    }>(),
    responses: {
      200: c.type<{
        filename: string;
        signature: {
          fields: {
            'content-type': string;
            key: string;
            policy: string;
            success_action_status: string;
            'x-goog-algorithm': string;
            'x-goog-credential': string;
            'x-goog-date': string;
            'x-goog-meta-account-id': string;
            'x-goog-meta-session-id': string;
            'x-goog-signature': string;
          };
          url: string;
        };
      }>(),
    },
    summary: 'Get signed URL for file upload to GCS',
  },
  getCopilotFileDownloadUrl: {
    method: 'GET',
    path: '/accounts/:accountId/copilot/:sessionId/files/:filename',
    pathParams: c.type<{ accountId: number; sessionId: string; filename: string }>(),
    responses: {
      200: c.type<{
        url: string;
      }>(),
    },
    summary: 'Get signed URL for file download from GCS',
  },
  updateCopilotSession: {
    method: 'PUT',
    path: '/accounts/:accountId/copilot/:sessionId',
    pathParams: c.type<{ accountId: number; sessionId: number }>(),
    body: c.type<{
      name: string;
    }>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Update copilot session name',
  },
  getCopilotSessions: {
    method: 'GET',
    path: '/accounts/:accountId/copilot',
    pathParams: c.type<{ accountId: number }>(),
    query: c.type<{ page?: number }>(),
    responses: {
      200: c.type<{
        meta: {
          pagination: {
            page: number;
            size: number;
            total: number;
          };
        };
        sessions: Array<{
          id: number;
          name: string;
          created_at: number;
          updated_at: number;
        }>;
      }>(),
    },
    summary: 'Get all copilot sessions',
  },
  recordCopilotFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/copilot/:sessionId/logs/:logId/feedback',
    pathParams: c.type<{ accountId: number; sessionId: number; logId: string }>(),
    body: c.type<{
      thumbs_up?: boolean;
      thumbs_down?: boolean;
      feedback_reason?: string;
    }>(),
    responses: {
      200: c.type<{
        success: boolean;
      }>(),
    },
    summary: 'Record feedback for a copilot response',
  },
  getAIContextOverrides: {
    method: 'GET',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<{ workspaceId: number }>(),
    responses: {
      200: c.type<{
        ai_context?: AIContext;
        overrides?: AIContextOverrides;
      }>(),
      500: c.type<{
        errors: Array<{
          detail: string;
          status: string;
        }>;
      }>(),
      400: c.type<{
        errors: Array<{
          detail: string;
          status: string;
        }>;
      }>(),
    },
    summary: 'Get AI context overrides',
  },
  updateAIContextOverrides: {
    method: 'PUT',
    path: '/environments/:workspaceId/ai_context/overrides',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{ overrides: AIContextOverrides }>(),
    responses: {
      200: c.type<{
        ai_context?: AIContext;
        overrides?: AIContextOverrides;
      }>(),
      500: c.type<{
        errors: Array<{
          detail: string;
          status: string;
        }>;
      }>(),
    },
    summary: 'Update AI context overrides',
  },

  /**
   * Feedback API
   */
  submitFeedback: {
    method: 'POST',
    path: '/accounts/:accountId/feedback',
    pathParams: c.type<{ accountId: number }>(),
    body: c.type<{
      replyTo: string;
      accountId: number;
      accountEmail?: string;
      userFirstName: string;
      userLastName: string;
      browser: string;
      url: string;
      windowDimensions: string;
      criticism: string;
      praise: string;
    }>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Submit user feedback',
  },

  /**
   * OAuth2 MCP API
   */
  oauthMcp: {
    method: 'POST',
    path: '/oauth2/mcp',
    contentType: 'application/x-www-form-urlencoded',
    body: c.type<string>(),
    responses: {
      200: c.type<{
        location: string;
      }>(),
      400: c.type<{
        errors: Array<{
          detail: string;
          status: string;
        }>;
      }>(),
      500: c.type<{
        errors: Array<{
          detail: string;
          status: string;
        }>;
      }>(),
    },
    summary: 'Handle OAuth2 authorization for MCP',
  },

  /**
   * Zendesk Chat API
   */
  getZendeskChatAccessToken: {
    method: 'GET',
    path: '/accounts/:accountId/users/:userId/zendesk_chat',
    pathParams: c.type<{ accountId: number; userId: number }>(),
    responses: {
      200: c.type<{
        token: string;
        is_handover_allowed: boolean;
        token_lifetime_minutes: number;
      }>(),
    },
    summary: 'Get Zendesk chat access token and configuration',
  },
  refreshZendeskChatToken: {
    method: 'POST',
    path: '/accounts/:accountId/users/:userId/zendesk_chat/token',
    pathParams: c.type<{ accountId: number; userId: number }>(),
    body: c.type<void>(),
    responses: {
      200: c.type<{
        token: string;
      }>(),
    },
    summary: 'Generate new Zendesk chat token',
  },

  /**
   * Tracking API
   */
  trackEvent: {
    method: 'POST',
    path: '/environments/:workspaceId/track_event',
    pathParams: c.type<{ workspaceId: number }>(),
    body: c.type<{
      properties: Record<string, unknown>;
      event: string;
    }>(),
    responses: {
      200: c.type<void>(),
    },
    summary: 'Track an event',
  },

  /**
   * Audit Logs API
   */
  getAllUsers: {
    method: 'GET',
    path: '/accounts/:account_id/all_users',
    pathParams: c.type<{ account_id: number }>(),
    responses: {
      200: c.type<{
        users: Array<{
          user_id: number;
          email: string;
          deleted?: boolean;
        }>;
      }>(),
    },
    summary: 'Get all users for an account',
  },
  getAccountAuditLogs: {
    method: 'GET',
    path: '/accounts/:account_id/audit_logs',
    pathParams: c.type<{ account_id: number }>(),
    query: c.type<{
      limit?: number;
      sort_order?: 'asc' | 'desc';
      cursor?: string;
      user_id?: number;
      ip_addr?: string;
      object_type?: string;
      object_id?: string;
      action?: string;
      attribute_name?: string;
      start_time?: string;
      end_time?: string;
    }>(),
    responses: {
      200: c.type<{
        data: Array<{
          id: number;
          timestamp: number;
          account_id: number;
          user_id: number;
          ip_addr: string;
          object_type: string;
          object_id: string;
          version: number;
          action: string;
          data: unknown;
          meta?: unknown;
        }>;
        pagination: {
          limit: number;
          sort_order: string;
          next_cursor: number;
        };
        users: Array<{
          user_id: number;
          email: string;
          deleted?: boolean;
        }>;
      }>(),
    },
    summary: 'Get audit logs for an account',
  },
  getWorkspaceAuditLogs: {
    method: 'GET',
    path: '/environments/:environment_id/audit_logs',
    pathParams: c.type<{ environment_id: number }>(),
    query: c.type<{
      limit?: number;
      sort_order?: 'asc' | 'desc';
      cursor?: string;
      user_id?: number;
      ip_addr?: string;
      object_type?: string;
      object_id?: string;
      action?: string;
      attribute_name?: string;
      start_time?: string;
      end_time?: string;
    }>(),
    responses: {
      200: c.type<{
        data: Array<{
          id: number;
          timestamp: number;
          account_id: number;
          user_id: number;
          ip_addr: string;
          object_type: string;
          object_id: string;
          version: number;
          action: string;
          data: unknown;
          meta?: unknown;
        }>;
        pagination: {
          limit: number;
          sort_order: string;
          next_cursor: number;
        };
        users: Array<{
          user_id: number;
          email: string;
          deleted?: boolean;
        }>;
      }>(),
    },
    summary: 'Get audit logs for a workspace',
  },
  getAccountAuditLogById: {
    method: 'GET',
    path: '/accounts/:account_id/audit_log/:id',
    pathParams: c.type<{ account_id: number; id: number }>(),
    responses: {
      200: c.type<{
        data: {
          id: number;
          timestamp: number;
          account_id: number;
          user_id: number;
          ip_addr: string;
          object_type: string;
          object_id: string;
          version: number;
          action: string;
          data: unknown;
          meta?: unknown;
        };
        user?: {
          user_id: number;
          email: string;
          deleted?: boolean;
        };
      }>(),
    },
    summary: 'Get a single audit log by ID for an account',
  },
  getWorkspaceAuditLogById: {
    method: 'GET',
    path: '/environments/:environment_id/audit_log/:id',
    pathParams: c.type<{ environment_id: number; id: number }>(),
    responses: {
      200: c.type<{
        data: {
          id: number;
          timestamp: number;
          account_id: number;
          user_id: number;
          ip_addr: string;
          object_type: string;
          object_id: string;
          version: number;
          action: string;
          data: unknown;
          meta?: unknown;
        };
        user?: {
          user_id: number;
          email: string;
          deleted?: boolean;
        };
      }>(),
    },
    summary: 'Get a single audit log by ID for a workspace',
  },
});

export type Contract = typeof contract;

const { api, query } = createApi(contract, {
  baseUrl: '/v1',
});

const augmentedApi: typeof api = {
  ...api,
  getAccountById: async (...params: Parameters<typeof api.getAccountById>) => {
    const accountId = params[0].params.accountId;
    if (accountId != null) {
      const cachedAccount = crossAppCache.getValue(`/accounts/${accountId}`);
      if (cachedAccount != null) {
        return cachedAccount;
      }
    }

    const response = await api.getAccountById(...params);

    // Sanity check, then cache response for use in other apps
    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/${accountId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
  getAccountByEnvironmentId: async (...params: Parameters<typeof api.getAccountByEnvironmentId>) => {
    const environmentId = params[0].params.environmentId;

    if (environmentId != null) {
      const cachedAccount = crossAppCache.getValue(`/account_by_environment/${environmentId}`);
      if (cachedAccount != null) {
        return cachedAccount;
      }
    }

    const response = await api.getAccountByEnvironmentId(...params);

    // Sanity check, then cache response for use in other apps
    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/account_by_environment/${environmentId}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }
    return response;
  },
  getAccount: async (...params: Parameters<typeof api.getAccount>) => {
    const cachedAccount = crossAppCache.getValue(`/accounts/current`);
    if (cachedAccount != null) {
      return cachedAccount;
    }

    const response = await api.getAccount(...params);

    // Sanity check, then cache response for use in other apps
    if (response?.account?.id != null) {
      crossAppCache.setValue({
        key: `/accounts/current`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });

      crossAppCache.setValue({
        key: `/accounts/${response.account.id}`,
        value: response,
        ttl: 10000,
        cacheOnlyOnce: true,
      });
    }

    return response;
  },
};

export { augmentedApi as api, query };
